import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, GBP } from "@/lib/api";
import { FileText, Download, ArrowRight, BellRing, Star, Flame } from "lucide-react";

function FeaturedHero({ product }) {
  if (!product) return null;
  return (
    <Link to={`/store/${product.id}`} className="group block mb-12 border border-[var(--ink)] bg-[var(--ink)] text-white overflow-hidden" data-testid="store-featured-hero">
      <div className="grid md:grid-cols-2">
        <div className="aspect-[4/3] md:aspect-auto bg-black overflow-hidden flex items-center justify-center relative">
          {product.cover_url
            ? <img src={product.cover_url} alt={product.title} className="w-full h-full object-cover md:object-contain group-hover:scale-[1.02] transition-transform" />
            : <FileText size={64} className="text-white/40" />}
        </div>
        <div className="p-8 sm:p-10 flex flex-col justify-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-label text-[var(--accent)] mb-3"><Star size={13} className="fill-[var(--accent)]" /> Featured{product.popular && !product.coming_soon && <span className="inline-flex items-center gap-1 ml-2"><Flame size={12} /> Most popular</span>}</span>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight mb-3">{product.title}</h2>
          {product.description && <p className="text-white/70 text-sm leading-relaxed mb-5 line-clamp-3">{product.description}</p>}
          <div className="flex items-center gap-4 mt-auto">
            <span className="font-display font-extrabold text-2xl">{GBP(product.price)}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)] group-hover:gap-2.5 transition-all">
              {product.coming_soon ? <><BellRing size={15} /> Get notified</> : <><Download size={15} /> Buy now</>} <ArrowRight size={15} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Store() {
  const [products, setProducts] = useState(null);

  useEffect(() => { api.get("/store/products").then((r) => setProducts(r.data)).catch(() => setProducts([])); }, []);

  const featured = products?.find((p) => p.featured) || null;
  const rest = products?.filter((p) => !featured || p.id !== featured.id) || [];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12" data-testid="store-page">
      <p className="text-xs tracking-label uppercase font-semibold text-[var(--accent)]">Digital store</p>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-1 mb-2">Ebooks, reports &amp; resources</h1>
      <p className="text-[var(--ink-2)] max-w-2xl mb-10">Instant digital downloads. Buy securely and get your file straight away.</p>

      {products === null && <div className="py-20 text-center text-[var(--ink-2)]">Loading…</div>}
      {products && products.length === 0 && (
        <div className="border border-[var(--line)] bg-white py-20 text-center text-[var(--ink-2)]" data-testid="store-empty">
          No products available yet. Check back soon.
        </div>
      )}

      <FeaturedHero product={featured} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((p) => (
          <Link key={p.id} to={`/store/${p.id}`} className="group border border-[var(--line)] bg-white flex flex-col hover:border-[var(--ink)] transition-colors" data-testid={`store-card-${p.id}`}>
            <div className="aspect-[3/4] bg-[var(--alt)] overflow-hidden flex items-center justify-center relative">
              {p.coming_soon && (
                <span className="absolute top-2 left-2 z-10 bg-[var(--ink)] text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1" data-testid={`store-coming-soon-${p.id}`}>Coming soon</span>
              )}
              {p.popular && !p.coming_soon && (
                <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-[var(--accent)] text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-1" data-testid={`store-popular-${p.id}`}><Flame size={11} /> Most popular</span>
              )}
              {p.cover_url
                ? <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                : <FileText className="text-[var(--ink-2)]" size={48} />}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <span className="text-xs uppercase tracking-wide text-[var(--ink-2)] font-semibold">{p.category}</span>
              <h3 className="font-display font-bold leading-tight mt-1 mb-2">{p.title}</h3>
              <div className="mt-auto flex items-center justify-between pt-2">
                <span className="font-display font-extrabold text-lg">{GBP(p.price)}</span>
                {p.coming_soon
                  ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ink-2)] uppercase tracking-wide">Coming soon</span>
                  : <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--accent)]"><Download size={15} /> Buy</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
